import { z } from 'zod'

/**
 * Agent friction reports (docs: guides/friction). A report describes a
 * durable, repeatable gap that cost an agent time: in the repository, in its
 * environment, or in Trevize itself. This schema is the single source for
 * the server, the CLI, the local MCP tool and the cloud sandbox tool.
 */

export const FRICTION_SCOPES = ['repo', 'environment', 'trevize'] as const

export type FrictionScope = (typeof FRICTION_SCOPES)[number]

export const FRICTION_STATUSES = [
  'open',
  'routed',
  'fixing',
  'resolved',
  'dismissed',
] as const

export type FrictionStatus = (typeof FRICTION_STATUSES)[number]

/** Statuses that still collect new reports; others get a fresh item. */
export const ACTIVE_FRICTION_STATUSES = [
  'open',
  'routed',
  'fixing',
] as const satisfies readonly FrictionStatus[]

export const FRICTION_SOURCES = ['cloud', 'local'] as const

export type FrictionSource = (typeof FRICTION_SOURCES)[number]

// OpenCode serializes omitted optional MCP string fields as empty strings.
function omitEmptyString(value: unknown): unknown {
  return typeof value === 'string' && value.trim() === '' ? undefined : value
}

function optionalText(max: number) {
  // The outer `optional()` keeps the key optional in the input type too.
  return z
    .preprocess(omitEmptyString, z.string().trim().max(max).optional())
    .optional()
}

export const FrictionCostSchema = z.object({
  minutes: z
    .number()
    .nonnegative()
    .max(1440)
    .optional()
    .describe('Roughly how many minutes it cost.'),
  retries: z
    .number()
    .int()
    .nonnegative()
    .max(1000)
    .optional()
    .describe('How many retries or failed attempts it took.'),
  note: optionalText(500).describe('Any other cost, in a sentence.'),
})

export type FrictionCost = z.infer<typeof FrictionCostSchema>

export const FrictionAgentSchema = z.object({
  harness: optionalText(50).describe(
    'Agent harness, e.g. "Claude Code", "Codex" or "OpenCode".',
  ),
  model: optionalText(100).describe('Model id, e.g. "claude-opus-5-5".'),
  effort: optionalText(20).describe(
    'Reasoning effort, e.g. "low", "medium" or "high".',
  ),
})

export type FrictionAgent = z.infer<typeof FrictionAgentSchema>

export const FrictionReportSchema = z.object({
  scope: z
    .enum(FRICTION_SCOPES)
    .describe(
      '`repo`: the code, docs, scripts or config of the repository. `environment`: setup, secrets, services, slow or failing installs. `trevize`: a Trevize tool or step got in the way.',
    ),
  title: z
    .string()
    .trim()
    .min(8)
    .max(120)
    .describe(
      'Short imperative summary, e.g. "bun install fails without the registry token".',
    ),
  whatHappened: z
    .string()
    .trim()
    .min(1)
    .max(2000)
    .describe('What happened, with the error or output you saw.'),
  wanted: z
    .string()
    .trim()
    .min(1)
    .max(1000)
    .describe('What you expected or needed instead.'),
  reproduction: z
    .string()
    .trim()
    .max(2000)
    .describe('Steps or the exact command that reproduces it.'),
  cost: FrictionCostSchema.describe(
    'What it cost: minutes, retries or a note.',
  ),
  investigation: optionalText(2000).describe(
    'Preliminary cause, if you looked into it.',
  ),
  relevantFiles: z
    .array(z.string().trim().min(1).max(500))
    .max(20)
    .optional()
    .describe('Repo-relative paths involved.'),
  tool: optionalText(200).describe(
    'Tool or command involved, e.g. "mcp__trevize__complete_step" or "bun install".',
  ),
  stepName: optionalText(200).describe('Blueprint step, when known.'),
  agent: FrictionAgentSchema.optional().describe(
    'The agent filing the report: harness, model and reasoning effort, when known.',
  ),
})

export type FrictionReport = z.infer<typeof FrictionReportSchema>

/**
 * JSON Schema of a report as tools receive it, for MCP `inputSchema`. Derived
 * from {@link FrictionReportSchema} so the CLI and sandbox tools never drift.
 */
export function frictionReportInputSchema(): {
  type: 'object'
  properties: { [name: string]: z.core.JSONSchema.JSONSchema }
  required: string[]
} {
  const { properties = {}, required = [] } = z.toJSONSchema(
    FrictionReportSchema,
    { io: 'input' },
  )
  const objectProperties: { [name: string]: z.core.JSONSchema.JSONSchema } = {}
  for (const [name, property] of Object.entries(properties)) {
    if (typeof property === 'object') objectProperties[name] = property
  }
  return { type: 'object', properties: objectProperties, required }
}
